// Scanner table column preferences (BUY and SELL separately, per user, in this browser).
// The first PROTECTED columns of every table are always shown, in place, and never listed for moving or hiding.
// Pure functions here; the hook and menu live in components/options/ScannerTable.js.

export const PREFS_VERSION = 1;

export const prefsKey = (userId, table) => `gz.scanner.columns.v${PREFS_VERSION}.${userId || "anon"}.${table}`;

/** The default arrangement: every optional column in definition order, hidden where `defaultHidden`. */
export function defaultPrefs(optional) {
  return { order: optional.map((c) => c.id), hidden: optional.filter((c) => c.defaultHidden).map((c) => c.id) };
}

/** Saved prefs reconciled with today's columns: unknown ids dropped, new columns added at their default place
 *  (and default visibility), so a release that adds or removes a column never breaks a saved layout. */
export function mergePrefs(saved, optional) {
  const def = defaultPrefs(optional);
  if (!saved || !Array.isArray(saved.order)) return def;
  const ids = new Set(def.order);
  const order = saved.order.filter((id) => ids.has(id));
  const known = new Set(saved.known || saved.order);
  def.order.forEach((id, i) => {
    if (order.includes(id)) return;
    const prev = def.order.slice(0, i).reverse().find((p) => order.includes(p));
    order.splice(prev ? order.indexOf(prev) + 1 : 0, 0, id);
  });
  const hidden = (saved.hidden || []).filter((id) => ids.has(id));
  def.hidden.forEach((id) => { if (!known.has(id) && !hidden.includes(id)) hidden.push(id); });
  return { order, hidden };
}

/** The columns to render: protected first (always, in place), then the visible optional ones in the saved order. */
export function visibleColumns(protectedCols, optional, prefs) {
  const byId = Object.fromEntries(optional.map((c) => [c.id, c]));
  const hidden = new Set(prefs.hidden);
  return [...protectedCols, ...prefs.order.filter((id) => !hidden.has(id) && byId[id]).map((id) => byId[id])];
}

export function moveColumn(prefs, id, toIndex) {
  const order = prefs.order.filter((x) => x !== id);
  order.splice(Math.max(0, Math.min(toIndex, order.length)), 0, id);
  return { ...prefs, order };
}

export function toggleColumn(prefs, id) {
  const hidden = prefs.hidden.includes(id) ? prefs.hidden.filter((x) => x !== id) : [...prefs.hidden, id];
  return { ...prefs, hidden };
}

export function loadPrefs(key, optional) {
  try {
    const raw = window.localStorage.getItem(key);
    return mergePrefs(raw ? JSON.parse(raw) : null, optional);
  } catch (e) {
    return defaultPrefs(optional);
  }
}

export function savePrefs(key, prefs) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ...prefs, known: prefs.order }));
  } catch (e) { /* storage unavailable: the layout still works for this visit */ }
}
